import type { RouteLocationNormalizedLoaded } from "vue-router";
import { defineStore } from "pinia";
import { DEFAULT_AUTHENTICATED_PATH, FORBIDDEN_PATH, LOGIN_PATH } from "@/app/router/constants";
import { useRouteCacheStore } from "@/shared/store/route-cache";

export interface PageTab {
  key: string;
  path: string;
  fullPath: string;
  name?: string;
  title: string;
  affix?: boolean;
  closable?: boolean;
  pinned?: boolean;
}

interface PageTabsState {
  tabs: PageTab[];
}

const HOME_TAB: PageTab = {
  key: "Dashboard",
  path: DEFAULT_AUTHENTICATED_PATH,
  fullPath: DEFAULT_AUTHENTICATED_PATH,
  name: "Dashboard",
  title: "系统首页",
  affix: true,
  closable: false,
};

const EXCLUDED_TAB_PATHS = new Set([LOGIN_PATH, FORBIDDEN_PATH, "/500"]);
const EXCLUDED_TAB_NAMES = new Set(["Login", "Forbidden", "ServerError", "NotFound"]);

function isSameTab(tab: PageTab, tabKey: string) {
  return tab.key === tabKey;
}

function isTabClosable(tab: PageTab) {
  return tab.closable !== false && tab.pinned !== true;
}

function normalizeRouteName(name: RouteLocationNormalizedLoaded["name"]) {
  return typeof name === "string" ? name : undefined;
}

export function getRouteTabKey(route: RouteLocationNormalizedLoaded) {
  return normalizeRouteName(route.name) || route.path;
}

function normalizeTab(tab: PageTab): PageTab {
  return {
    ...tab,
    key: tab.key || tab.name || tab.path,
    fullPath: tab.fullPath || tab.path,
    title: tab.title || tab.path,
    affix: false,
    closable: tab.closable ?? true,
    pinned: tab.pinned === true,
  };
}

function createTabFromRoute(route: RouteLocationNormalizedLoaded): PageTab | null {
  const routeName = normalizeRouteName(route.name);

  if (
    route.path === HOME_TAB.path ||
    EXCLUDED_TAB_PATHS.has(route.path) ||
    (routeName && EXCLUDED_TAB_NAMES.has(routeName))
  ) {
    return null;
  }

  const title = typeof route.meta.title === "string" ? route.meta.title : routeName || route.path;

  return normalizeTab({
    key: routeName || route.path,
    path: route.path,
    fullPath: route.fullPath || route.path,
    name: routeName,
    title,
  });
}

export const usePageTabsStore = defineStore("page-tabs", {
  state: (): PageTabsState => ({
    tabs: [],
  }),
  getters: {
    homeTab: () => HOME_TAB,
  },
  actions: {
    normalizeTabs() {
      const normalizedTabs = this.tabs
        .map(normalizeTab)
        .filter((tab) => tab.path && tab.title && tab.path !== HOME_TAB.path);
      const uniqueTabs = new Map<string, PageTab>();

      normalizedTabs.forEach((tab) => uniqueTabs.set(tab.key, tab));
      const uniqueTabList = [...uniqueTabs.values()];
      this.tabs = [
        ...uniqueTabList.filter((tab) => tab.pinned),
        ...uniqueTabList.filter((tab) => !tab.pinned),
      ];
    },
    addRouteTab(route: RouteLocationNormalizedLoaded) {
      const tab = createTabFromRoute(route);

      this.normalizeTabs();

      if (!tab) {
        return null;
      }

      const existingIndex = this.tabs.findIndex((item) => isSameTab(item, tab.key));

      if (existingIndex >= 0) {
        const existingTab = this.tabs[existingIndex];

        this.tabs[existingIndex] = {
          ...existingTab,
          ...tab,
          affix: false,
          closable: tab.closable,
          pinned: existingTab?.pinned === true,
        };
        useRouteCacheStore().registerRoute(route, tab.key);
        return this.tabs[existingIndex];
      }

      this.tabs.push(tab);
      useRouteCacheStore().registerRoute(route, tab.key);
      return tab;
    },
    removeTab(targetKey: string, activeKey: string) {
      this.normalizeTabs();

      const targetIndex = this.tabs.findIndex((tab) => isSameTab(tab, targetKey));
      const targetTab = this.tabs[targetIndex];

      if (targetIndex < 0 || !targetTab || !isTabClosable(targetTab)) {
        return undefined;
      }

      const nextTab = this.getFallbackTab(targetIndex);
      this.tabs.splice(targetIndex, 1);
      useRouteCacheStore().removeTab(targetTab.key);

      return targetKey === activeKey ? nextTab : undefined;
    },
    closeLeft(targetKey: string, activeKey: string) {
      this.normalizeTabs();

      const targetIndex = this.tabs.findIndex((tab) => isSameTab(tab, targetKey));

      if (targetIndex <= 0) {
        return undefined;
      }

      const removedTabs = this.tabs.filter(
        (tab, index) => index < targetIndex && isTabClosable(tab),
      );
      const activeWillBeRemoved = removedTabs.some((tab) => tab.key === activeKey);

      this.tabs = this.tabs.filter((tab, index) => !isTabClosable(tab) || index >= targetIndex);
      useRouteCacheStore().removeTabs(removedTabs.map((tab) => tab.key));

      return activeWillBeRemoved
        ? this.tabs.find((tab) => tab.key === targetKey) || HOME_TAB
        : undefined;
    },
    closeRight(targetKey: string, activeKey: string) {
      this.normalizeTabs();

      const targetIndex = this.tabs.findIndex((tab) => isSameTab(tab, targetKey));

      if (targetIndex < 0 || targetIndex >= this.tabs.length - 1) {
        return undefined;
      }

      const removedTabs = this.tabs.filter(
        (tab, index) => index > targetIndex && isTabClosable(tab),
      );
      const activeWillBeRemoved = removedTabs.some((tab) => tab.key === activeKey);

      this.tabs = this.tabs.filter((tab, index) => !isTabClosable(tab) || index <= targetIndex);
      useRouteCacheStore().removeTabs(removedTabs.map((tab) => tab.key));

      return activeWillBeRemoved
        ? this.tabs.find((tab) => tab.key === targetKey) || HOME_TAB
        : undefined;
    },
    closeOther(targetKey: string, activeKey: string) {
      this.normalizeTabs();

      const targetTab = this.tabs.find((tab) => tab.key === targetKey);

      if (!targetTab) {
        return undefined;
      }

      const removedTabs = this.tabs.filter((tab) => isTabClosable(tab) && tab.key !== targetKey);
      const activeWillBeRemoved = removedTabs.some((tab) => tab.key === activeKey);
      this.tabs = this.tabs.filter((tab) => !isTabClosable(tab) || tab.key === targetKey);
      useRouteCacheStore().removeTabs(removedTabs.map((tab) => tab.key));

      return activeWillBeRemoved ? targetTab : undefined;
    },
    closeAll(activeKey: string) {
      this.normalizeTabs();

      const removedTabs = this.tabs.filter(isTabClosable);
      const activeWillBeRemoved = removedTabs.some((tab) => tab.key === activeKey);
      this.tabs = this.tabs.filter((tab) => !isTabClosable(tab));
      useRouteCacheStore().removeTabs(removedTabs.map((tab) => tab.key));

      return activeWillBeRemoved ? HOME_TAB : undefined;
    },
    setTabPinned(targetKey: string, pinned: boolean) {
      this.normalizeTabs();

      const targetIndex = this.tabs.findIndex((tab) => isSameTab(tab, targetKey));
      const targetTab = this.tabs[targetIndex];

      if (targetIndex < 0 || !targetTab || targetTab.pinned === pinned) {
        return;
      }

      const [movedTab] = this.tabs.splice(targetIndex, 1);

      if (!movedTab) {
        return;
      }

      movedTab.pinned = pinned;
      const pinnedCount = this.tabs.filter((tab) => tab.pinned).length;
      this.tabs.splice(pinnedCount, 0, movedTab);
    },
    clearTabs() {
      this.tabs = [];
      useRouteCacheStore().clearAll();
    },
    retainRouteNames(routeNames: string[]) {
      this.normalizeTabs();

      const validNames = new Set(routeNames);
      const removedTabs = this.tabs.filter((tab) => !tab.name || !validNames.has(tab.name));
      this.tabs = this.tabs.filter((tab) => tab.name && validNames.has(tab.name));

      const routeCacheStore = useRouteCacheStore();
      routeCacheStore.removeTabs(removedTabs.map((tab) => tab.key));
    },
    getFallbackTab(targetIndex: number) {
      return this.tabs[targetIndex - 1] || this.tabs[targetIndex + 1] || HOME_TAB;
    },
  },
  persist: {
    key: "page-tabs",
    pick: ["tabs"],
  },
});
